import { useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { handleLibraryRouteDragEnd, librarySidebarDndUiRef } from "@/library/libraryRouteDragEnd";

type LibraryRouteDndWrapperProps = {
  /** When false, children render without DndContext (non-library routes). */
  when: boolean;
  children: React.ReactNode;
};

export function LibraryRouteDndWrapper({ when, children }: LibraryRouteDndWrapperProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragStart = useCallback((event: DragStartEvent) => {
    librarySidebarDndUiRef.current?.onDragStart(event);
  }, []);

  const onDragOver = useCallback((event: DragOverEvent) => {
    librarySidebarDndUiRef.current?.onDragOver(event);
  }, []);

  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      librarySidebarDndUiRef.current?.resetUi();
      await handleLibraryRouteDragEnd(event, { queryClient, toast });
    },
    [queryClient, toast]
  );

  if (!when) {
    return <>{children}</>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      {children}
    </DndContext>
  );
}
